import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminLicensePlans, updateLicensePlan } from '../services/api';
import '../styles/admin-ui.css';
import './AdminLicenses.css';

const currencySymbol = (currency) => {
  const symbols = { TRY: '₺', USD: '$', EUR: '€' };
  return symbols[currency] || currency || '₺';
};

const AdminLicenses = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminLicensePlans();
      const list = Array.isArray(data) ? data : data?.plans || [];
      setPlans(list);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
      setError(err.message || 'Failed to load license plans');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setSaveError(null);
    setEditingPlan({
      ...plan,
      features: plan.features || [],
      newFeature: '',
    });
  };

  const handleSave = async () => {
    if (!editingPlan) return;

    setSaving(true);
    setSaveError(null);
    try {
      await updateLicensePlan(editingPlan.id, {
        display_name: editingPlan.display_name,
        description: editingPlan.description,
        price_monthly: parseFloat(editingPlan.price_monthly) || 0,
        price_yearly: parseFloat(editingPlan.price_yearly) || 0,
        currency: editingPlan.currency,
        max_connections: parseInt(editingPlan.max_connections, 10) || 1,
        max_users: parseInt(editingPlan.max_users, 10) || 1,
        max_devices_per_user: parseInt(editingPlan.max_devices_per_user, 10) || 1,
        bandwidth_limit_mbps: parseInt(editingPlan.bandwidth_limit_mbps, 10) || 0,
        features: editingPlan.features,
        is_highlighted: editingPlan.is_highlighted,
        is_active: editingPlan.is_active,
      });

      setEditingPlan(null);
      fetchPlans();
    } catch (err) {
      console.error('Failed to save plan:', err);
      setSaveError(err.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (editingPlan.newFeature.trim()) {
      setEditingPlan({
        ...editingPlan,
        features: [...editingPlan.features, editingPlan.newFeature.trim()],
        newFeature: '',
      });
    }
  };

  const removeFeature = (index) => {
    setEditingPlan({
      ...editingPlan,
      features: editingPlan.features.filter((_, i) => i !== index),
    });
  };

  const formatPrice = (plan) => {
    const symbol = currencySymbol(plan.currency);
    if (plan.price_monthly > 0) {
      return { price: `${symbol}${plan.price_monthly}`, period: '/month' };
    }
    if (plan.plan_key === 'FREE') {
      return { price: 'Free', period: null };
    }
    return { price: 'Contact us', period: null };
  };

  if (loading) {
    return (
      <div className="aui-page licenses-page">
        <div className="aui-empty">
          <div className="aui-spinner" />
          <p>Loading license plans…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aui-page licenses-page">
      <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
        ← Back to Admin
      </button>

      <div className="aui-hero">
        <div>
          <p className="aui-kicker">Billing</p>
          <h1>License plans</h1>
          <p className="aui-lead">
            Configure pricing, limits, and features for each plan. Create and delete are not available from the API.
          </p>
        </div>
        <div className="aui-hero-actions">
          <button type="button" className="aui-btn ghost" onClick={fetchPlans}>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="aui-error">
          <span>{error}</span>
          <button type="button" className="aui-btn ghost" onClick={fetchPlans}>
            Retry
          </button>
        </div>
      )}

      <div className="aui-stats">
        <article className="aui-stat">
          <span>Plans</span>
          <strong>{plans.length}</strong>
        </article>
        <article className="aui-stat">
          <span>Active</span>
          <strong>{plans.filter((p) => p.is_active).length}</strong>
        </article>
        <article className="aui-stat">
          <span>Highlighted</span>
          <strong>{plans.filter((p) => p.is_highlighted).length}</strong>
        </article>
      </div>

      {plans.length === 0 ? (
        <div className="aui-empty">
          <h3>No plans</h3>
          <p>No license plans were returned by the API.</p>
        </div>
      ) : (
        <div className="aui-grid cards-2">
          {plans.map((plan) => {
            const pricing = formatPrice(plan);
            return (
              <article
                key={plan.id}
                className={`aui-card license-card ${plan.is_highlighted ? 'is-highlighted' : ''} ${
                  !plan.is_active ? 'is-inactive' : ''
                }`}
              >
                <div className="license-card-head">
                  <div>
                    <h3>{plan.display_name}</h3>
                    <div className="license-card-chips">
                      <span className="aui-chip signal">{plan.plan_key}</span>
                      {plan.is_highlighted && <span className="aui-chip warn">Popular</span>}
                      {!plan.is_active && <span className="aui-chip danger">Inactive</span>}
                      {plan.is_active && <span className="aui-chip ok">Active</span>}
                    </div>
                  </div>
                  <div className="license-price">
                    <strong>{pricing.price}</strong>
                    {pricing.period && <span>{pricing.period}</span>}
                  </div>
                </div>

                {plan.description && <p>{plan.description}</p>}

                <dl className="license-limits">
                  <div>
                    <dt>Connections</dt>
                    <dd>{plan.max_connections}</dd>
                  </div>
                  <div>
                    <dt>Users</dt>
                    <dd>{plan.max_users}</dd>
                  </div>
                  <div>
                    <dt>Devices / user</dt>
                    <dd>{plan.max_devices_per_user}</dd>
                  </div>
                  <div>
                    <dt>Bandwidth</dt>
                    <dd>
                      {plan.bandwidth_limit_mbps === 0
                        ? 'Unlimited'
                        : `${plan.bandwidth_limit_mbps} Mbps`}
                    </dd>
                  </div>
                </dl>

                {(plan.features || []).length > 0 && (
                  <ul className="license-features">
                    {plan.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                )}

                <button type="button" className="aui-btn primary" onClick={() => handleEdit(plan)}>
                  Edit plan
                </button>
              </article>
            );
          })}
        </div>
      )}

      {editingPlan && (
        <div className="aui-modal-overlay" onClick={() => setEditingPlan(null)}>
          <div className="aui-modal wide" onClick={(e) => e.stopPropagation()}>
            <h2>Edit {editingPlan.plan_key}</h2>
            <p className="aui-lead" style={{ marginBottom: '1rem' }}>
              Plan key is fixed. Currency follows the plan record ({editingPlan.currency || 'TRY'}).
            </p>

            {saveError && <div className="aui-error compact">{saveError}</div>}

            <div className="aui-form">
              <div className="license-form-row">
                <label>
                  Display name
                  <input
                    type="text"
                    value={editingPlan.display_name}
                    onChange={(e) =>
                      setEditingPlan({ ...editingPlan, display_name: e.target.value })
                    }
                  />
                </label>
                <label>
                  Plan key
                  <input type="text" value={editingPlan.plan_key} disabled />
                </label>
              </div>

              <label>
                Description
                <textarea
                  value={editingPlan.description || ''}
                  onChange={(e) =>
                    setEditingPlan({ ...editingPlan, description: e.target.value })
                  }
                  rows={2}
                />
              </label>

              <div className="license-form-row three">
                <label>
                  Monthly price
                  <input
                    type="number"
                    step="0.01"
                    value={editingPlan.price_monthly}
                    onChange={(e) =>
                      setEditingPlan({ ...editingPlan, price_monthly: e.target.value })
                    }
                  />
                </label>
                <label>
                  Yearly price
                  <input
                    type="number"
                    step="0.01"
                    value={editingPlan.price_yearly}
                    onChange={(e) =>
                      setEditingPlan({ ...editingPlan, price_yearly: e.target.value })
                    }
                  />
                </label>
                <label>
                  Currency
                  <select
                    value={editingPlan.currency || 'TRY'}
                    onChange={(e) =>
                      setEditingPlan({ ...editingPlan, currency: e.target.value })
                    }
                  >
                    <option value="TRY">TRY (₺)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </label>
              </div>

              <div className="license-form-row four">
                <label>
                  Max connections
                  <input
                    type="number"
                    value={editingPlan.max_connections}
                    onChange={(e) =>
                      setEditingPlan({ ...editingPlan, max_connections: e.target.value })
                    }
                  />
                </label>
                <label>
                  Max users
                  <input
                    type="number"
                    value={editingPlan.max_users}
                    onChange={(e) =>
                      setEditingPlan({ ...editingPlan, max_users: e.target.value })
                    }
                  />
                </label>
                <label>
                  Devices / user
                  <input
                    type="number"
                    value={editingPlan.max_devices_per_user}
                    onChange={(e) =>
                      setEditingPlan({
                        ...editingPlan,
                        max_devices_per_user: e.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Bandwidth (Mbps)
                  <input
                    type="number"
                    value={editingPlan.bandwidth_limit_mbps}
                    onChange={(e) =>
                      setEditingPlan({
                        ...editingPlan,
                        bandwidth_limit_mbps: e.target.value,
                      })
                    }
                    placeholder="0 = Unlimited"
                  />
                </label>
              </div>

              <div className="license-features-editor">
                <span className="license-features-label">Features</span>
                <div className="license-feature-chips">
                  {editingPlan.features.map((feature, idx) => (
                    <span key={idx} className="aui-chip signal license-feature-chip">
                      {feature}
                      <button type="button" onClick={() => removeFeature(idx)} aria-label="Remove">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="license-add-feature">
                  <input
                    type="text"
                    value={editingPlan.newFeature}
                    onChange={(e) =>
                      setEditingPlan({ ...editingPlan, newFeature: e.target.value })
                    }
                    placeholder="Add feature…"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <button type="button" className="aui-btn ghost" onClick={addFeature}>
                    Add
                  </button>
                </div>
              </div>

              <div className="license-toggles">
                <label className="license-toggle">
                  <input
                    type="checkbox"
                    checked={!!editingPlan.is_highlighted}
                    onChange={(e) =>
                      setEditingPlan({
                        ...editingPlan,
                        is_highlighted: e.target.checked,
                      })
                    }
                  />
                  Highlighted (popular)
                </label>
                <label className="license-toggle">
                  <input
                    type="checkbox"
                    checked={!!editingPlan.is_active}
                    onChange={(e) =>
                      setEditingPlan({ ...editingPlan, is_active: e.target.checked })
                    }
                  />
                  Active (show on pricing page)
                </label>
              </div>

              <div className="aui-form-actions">
                <button
                  type="button"
                  className="aui-btn ghost"
                  onClick={() => setEditingPlan(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="aui-btn primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLicenses;
